import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { coreApi } from "./api/calls";

/**
 * Custom breadcrumb item with building name.
 *
 * @alias BuildingName
 * @param {object} match
 * @returns <span>
 */
const BuildingName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("buildings/name/" + match.params.building_id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("buildings.buildingOverviewHeader")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with building unit name.
 *
 * @alias BuildingUnitName
 * @param {object} match
 * @returns <span>
 */
const BuildingUnitName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("buildings/units/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("buildings.buildingUnitOverview")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with partner name.
 *
 * @alias PartnerName
 * @param {object} match
 * @returns <span>
 */
const PartnerName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("partners/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("partners.partnersOverviewHeader")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with client name.
 *
 * @alias ClientName
 * @param {object} match
 * @returns <span>
 */
const ClientName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("clients/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("clients.clientOverviewHeader")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with contact name.
 *
 * @alias ContactName
 * @param {object} match
 * @returns <span>
 */
const ContactName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("contacts/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("breadcrumbs.contactProfile")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with invoice name.
 *
 * @alias InvoiceName
 * @param {object} match
 * @returns <span>
 */
const InvoiceName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("invoices/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("breadcrumbs.invoiceProfile")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with account name.
 *
 * @alias AccountName
 * @param {object} match
 * @returns <span>
 */
const AccountName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("accounts/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("breadcrumbs.accountProfile")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with country name.
 *
 * @alias CountryName
 * @param {object} match
 * @returns <span>
 */
const CountryName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("countries/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("breadcrumbs.countryProfile")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with city name.
 *
 * @alias CityName
 * @param {object} match
 * @returns <span>
 */
const CityName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("cities/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("breadcrumbs.cityProfile")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with employee name.
 *
 * @alias EmployeeName
 * @param {object} match
 * @returns <span>
 */
const EmployeeName = ({ match }) => {
  // States
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  // Translations
  const { t } = useTranslation();

  // Fetch name
  useEffect(() => {
    coreApi
      .get("employees/name/" + match.params.id)
      .then((response) => {
        setName(response.data.name);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [match]);

  return loading ? <span>{t("breadcrumbs.employeeProfile")}</span> : <span>{name}</span>;
};

/**
 * Custom breadcrumb item with order name.
 *
 * @alias OrderName
 * @param {object} match
 * @returns <span>
 */
const OrderName = ({ match }) => {
  // Translations
  const { t } = useTranslation();

  return <span>{t("offers.order") + " " + match.params.id.padStart(6, "0")}</span>;
};

/**
 * Custom breadcrumb item with offer name.
 *
 * @alias OfferName
 * @param {object} match
 * @returns <span>
 */
const OfferName = ({ match }) => {
  // Translations
  const { t } = useTranslation();

  return <span>{t("offers.offer") + " " + match.params.id.padStart(6, "0")}</span>;
};

/**
 * Custom link for building's units breadcrumb item.
 *
 * @alias CustomUnitLink
 * @param {object} match
 * @returns <span>
 */
const CustomUnitLink = ({ match }) => {
  // Translations
  const { t } = useTranslation();

  return (
    <Link to={{ pathname: "/buildings/" + match.params.building_id, hash: "#units", state: { isNew: true } }}>
      {t("buildings.buildingUnitsOverview")}
    </Link>
  );
};

/**
 * Custom link for cities breadcrumb item.
 *
 * @alias CustomCityLink
 * @param {object} match
 * @returns <span>
 */
const CustomCityLink = ({ match }) => {
  // Translations
  const { t } = useTranslation();

  return (
    <Link to={{ pathname: "/countries/" + match.params.country_id, hash: "#cities", state: { isNew: true } }}>
      {t("expansion.cities")}
    </Link>
  );
};

/**
 * Custom link for orders breadcrumb item.
 *
 * @alias CustomOrderLink
 * @param {object} match
 * @returns <span>
 */
const CustomOrderLink = ({ match }) => {
  // Translations
  const { t } = useTranslation();

  return <Link to={{ pathname: "/offers" }}>{t("offers.orders")}</Link>;
};

/**
 * Make array from rules
 *
 * @returns array
 * @param cleaningGroup
 */
const ParseRegularityFromRrule = (cleaningGroup) => {
  let parsed = {};
  let rrule = cleaningGroup?.rrule_blueprint;
  if (!rrule) return null;
  rrule = rrule.replace("RRULE:", "");
  rrule = rrule.split(";");
  rrule.forEach((item) => {
    item = item.split("=");
    parsed[item[0]] = item[1];
  });

  if (!parsed.INTERVAL) {
    parsed.INTERVAL = 1;
  }

  if (parsed.BYDAY) {
    parsed.BYDAY = parsed.BYDAY.split(",");
    parsed.REPETITION_COUNT = parsed.BYDAY.length;
  }
  return parsed;
};

/**
 * Take days from rule
 *
 * @returns array
 * @param days
 * @param element
 */
const ParseRegularityFromRruleDays = (days, element) => {
  let extendedDays = element.cleaning_service_groups && element.cleaning_service_groups[0]
    ? ParseRegularityFromRrule(element.cleaning_service_groups[0])?.BYDAY || []
    : [];

  if (element.service_rrules && element.service_rrules[0]) {
    element.service_rrules.sort((a, b) => {
      const aIsWeeklyInterval = a.rrule?.startsWith('FREQ=WEEKLY;INTERVAL=');
      const bIsWeeklyInterval = b.rrule?.startsWith('FREQ=WEEKLY;INTERVAL=');
      if (aIsWeeklyInterval === bIsWeeklyInterval) {
        return 0;
      }
      return aIsWeeklyInterval ? -1 : 1;
    });

    element.service_rrules[0].rrule_blueprint = element.service_rrules[0].rrule
    let extendedDaysFromRules = ParseRegularityFromRrule(element.service_rrules[0])?.BYDAY;
    if (extendedDays.length <= extendedDaysFromRules.length || JSON.stringify(days) === JSON.stringify(extendedDaysFromRules)) extendedDays = extendedDaysFromRules;
  }

  if (Array.isArray(days)) {
    days = days.flatMap(day =>
      typeof day === "string" && day.includes(",") ? day.split(",") : [day]
    );
  }

  if (days && Array.isArray(days) && Array.isArray(extendedDays) && extendedDays.length > days.length) {
    days = extendedDays;
  }

  return days || [];
};

const GetCityNameById = ({ id, areaScope }) => {
  const countries = areaScope?.cities;

  if (!Array.isArray(countries) || !countries.length) {
    return null;
  }

  for (const country of countries) {
    const city = country.city_branches?.find(city => city.id === id);
    if (city) {
      return city.name;
    }
  }

  return null;
};

function IsBiweeklyBuildingUnit(buildingUnit) {
  if (!buildingUnit) return false;

  if (buildingUnit.cleaning_service_groups) {
    return buildingUnit.cleaning_service_groups?.some((group) =>
      group?.rrule_blueprint?.includes("WEEKLY") &&
      group?.rrule_blueprint?.includes("INTERVAL=2")
    );
  }

  if (buildingUnit.service_rrules) {
    return buildingUnit.service_rrules?.some((rrule) =>
      rrule?.rrule?.includes("WEEKLY") &&
      rrule?.rrule?.includes("INTERVAL=2")
    );
  }
}

/**
 * Extract filename from Content-Disposition header
 *
 * @param {object} response - Axios response object
 * @returns {string|null} - Extracted filename or null
 */
const GetFilenameFromResponse = (response) => {

  const contentDisposition = response?.headers?.["content-disposition"] || 
                             response?.headers?.["Content-Disposition"];
  
  if (!contentDisposition) {
    return null;
  }

  const standardMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  
  if (standardMatch && standardMatch[1]) {
    let filename = standardMatch[1].replace(/['"]/g, '');
    
    try {
      filename = decodeURIComponent(filename);
    } catch (e) {

    }
    
    return filename;
  }

  if (contentDisposition.match(/\.\w+$/)) {
    try {
      return decodeURIComponent(contentDisposition.trim());
    } catch (e) {
      return contentDisposition.trim();
    }
  }

  return null;
};


// Export of helpers
export {
  BuildingName,
  BuildingUnitName,
  CustomUnitLink,
  OfferName,
  OrderName,
  CustomOrderLink,
  PartnerName,
  ClientName,
  ContactName,
  InvoiceName,
  AccountName,
  CountryName,
  CustomCityLink,
  CityName,
  EmployeeName,
  ParseRegularityFromRrule,
  ParseRegularityFromRruleDays,
  GetCityNameById,
  IsBiweeklyBuildingUnit,
  GetFilenameFromResponse
};
